/**
 * Utilities for drawing and managing resize handles on objects
 */

import { apply3DRotationTo2D } from './geometryUtils';
import { handleSizing } from './handleSizing';
import { resizeDimensions } from '@shared-lib/geometry/resizeDimensions';
import {
  drawHandleDisc,
  GRAB_HANDLE_GAP_PX,
  GRAB_HANDLE_RADIUS_PX,
} from './grabHandleUtils';

// Handle types - Using numbers for corners and letters for sides
export const HANDLE_TYPES = {
  TOP_LEFT: '1', // Corner 1
  TOP_RIGHT: '2', // Corner 2
  BOTTOM_RIGHT: '3', // Corner 3
  BOTTOM_LEFT: '4', // Corner 4
  TOP_CENTER: 'A', // Side A (top)
  MIDDLE_RIGHT: 'B', // Side B (right)
  BOTTOM_CENTER: 'C', // Side C (bottom)
  MIDDLE_LEFT: 'D', // Side D (left)
};

/**
 * Get the bounding box of an object in 2D space
 * @param {Object} obj - Object with center, dimensions, and rotation
 * @returns {Object} Bounding box with corners and dimensions
 */
export const getObjectBoundingBox = (obj) => {
  if (!obj || !obj.center || !obj.dimensions) return null;

  const centerX = obj.center.x;
  const centerZ = obj.center.z;

  // For wall-mounted objects (wall-light and mirror), the dimensions are:
  // x = width along wall, y = height, z = depth projecting from wall
  // In the floor plan view, we see width (x) along the wall and depth (z) projecting out
  const objectType = obj.category?.['wall-light']
    ? 'wall-light'
    : obj.category?.mirror
      ? 'mirror'
      : obj.type?.toLowerCase();

  // NOTE: This is a LOCAL boolean controlling how (x, z) dimensions are
  // interpreted for bounding-box drawing, NOT the imported isWallMounted
  // helper from elevationConstants. It is intentionally limited to
  // wall-light + mirror — fixtures whose `z` dimension represents depth
  // projecting out from the wall. Wall-hung toilets are NOT covered here:
  // their dimensions follow the floor-fixture convention (footprint on
  // floor), so they continue to be treated as regular objects below.
  const isWallMounted = objectType === 'wall-light' || objectType === 'mirror';

  // For wall-mounted objects, use the dimensions as they are
  // For regular objects, use x as width and z as depth
  const width = obj.dimensions.x;
  const depth = obj.dimensions.z;

  // Get rotation in 2D
  const rotation2D = obj.rotation ? apply3DRotationTo2D(obj.rotation) : [1, 0];
  const angle = Math.atan2(rotation2D[1], rotation2D[0]);

  // Calculate corners before rotation
  const halfWidth = width / 2;
  const halfDepth = depth / 2;

  // Define corners in local space
  // Note: In screen space, negative Z appears at bottom, positive Z at top
  const localCorners = [
    { x: -halfWidth, z: -halfDepth }, // bottom-left (appears at bottom of screen)
    { x: halfWidth, z: -halfDepth }, // bottom-right
    { x: halfWidth, z: halfDepth }, // top-right (appears at top of screen)
    { x: -halfWidth, z: halfDepth }, // top-left
  ];

  // Rotate and translate corners
  const cos = Math.cos(angle);
  const sin = Math.sin(angle);

  const corners = localCorners.map((corner) => {
    const rotatedX = corner.x * cos - corner.z * sin;
    const rotatedZ = corner.x * sin + corner.z * cos;
    return {
      x: centerX + rotatedX,
      z: centerZ + rotatedZ,
    };
  });

  return {
    corners,
    center: { x: centerX, z: centerZ },
    width,
    depth,
    angle,
    rotation2D,
  };
};

/**
 * Check if a point is inside the object bounds (accounting for rotation)
 * @param {Object} point - {x, z} point in world space
 * @param {Object} obj - Object with center, dimensions, and rotation
 * @returns {boolean} True if point is inside object
 */
export const isPointInObject = (point, obj) => {
  const boundingBox = getObjectBoundingBox(obj);
  if (!boundingBox) return false;

  const { angle, center, width, depth } = boundingBox;

  // Transform point to object's local space
  const cos = Math.cos(-angle); // Negative angle for inverse rotation
  const sin = Math.sin(-angle);

  const relX = point.x - center.x;
  const relZ = point.z - center.z;

  const localX = relX * cos - relZ * sin;
  const localZ = relX * sin + relZ * cos;

  // Add minimum hit area for thin/zero-thickness elements
  const MIN_HIT_SIZE = 0.15; // 15cm minimum hit area

  // Check if point is within local bounds with padding
  const halfWidth = Math.max(width / 2, MIN_HIT_SIZE);
  const halfDepth = Math.max(depth / 2, MIN_HIT_SIZE);

  return (
    localX >= -halfWidth &&
    localX <= halfWidth &&
    localZ >= -halfDepth &&
    localZ <= halfDepth
  );
};

/**
 * Get handle positions for an object
 * @param {Object} boundingBox - Bounding box from getObjectBoundingBox
 * @returns {Object} Handle positions keyed by handle type
 */
export const getHandlePositions = (boundingBox, offsetWorld = 0) => {
  if (!boundingBox) return {};

  const { corners } = boundingBox;

  if (!corners || corners.length < 4) return {};
  const raw = rawHandlePositions(corners);
  if (!(offsetWorld > 0)) return raw;
  // Push every handle OUTWARD from the box centre by `offsetWorld`: a
  // corner along its diagonal, a side along its edge normal. This is
  // the finger layout — the disc sits beside the edge it resizes, not
  // under the finger. Safe because the resize math is delta-based
  // (`calculateResize` takes the drag delta, not the cursor).
  const out = {};
  for (const [type, p] of Object.entries(raw)) {
    const d = handleOutwardDirection(boundingBox, type);
    out[type] = { x: p.x + d.x * offsetWorld, z: p.z + d.z * offsetWorld };
  }
  return out;
};

/**
 * Unit vector pointing out of the box through a handle.
 */
export const handleOutwardDirection = (boundingBox, type) => {
  const p = rawHandlePositions(boundingBox.corners)[type];
  const { center } = boundingBox;
  const dx = p.x - center.x;
  const dz = p.z - center.z;
  const len = Math.hypot(dx, dz) || 1;
  return { x: dx / len, z: dz / len };
};

/** Screen-space offset of a finger handle from its edge, in world metres. */
export const coarseHandleOffset = (scale) =>
  (GRAB_HANDLE_GAP_PX + GRAB_HANDLE_RADIUS_PX) / scale;

const rawHandlePositions = (corners) => {
  // Update assignments to match screen layout
  // corners[0] = bottom-left (screen), corners[1] = bottom-right (screen),
  // corners[2] = top-right (screen), corners[3] = top-left (screen)

  const handles = {
    1: corners[0], // Originally bottom-left, let's keep original mapping
    2: corners[1], // Originally bottom-right
    3: corners[2], // Originally top-right
    4: corners[3], // Originally top-left
    A: {
      // Originally TOP (between 0 and 1, which is bottom on screen)
      x: (corners[0].x + corners[1].x) / 2,
      z: (corners[0].z + corners[1].z) / 2,
    },
    B: {
      // RIGHT
      x: (corners[1].x + corners[2].x) / 2,
      z: (corners[1].z + corners[2].z) / 2,
    },
    C: {
      // Originally BOTTOM (between 2 and 3, which is top on screen)
      x: (corners[2].x + corners[3].x) / 2,
      z: (corners[2].z + corners[3].z) / 2,
    },
    D: {
      // LEFT
      x: (corners[3].x + corners[0].x) / 2,
      z: (corners[3].z + corners[0].z) / 2,
    },
  };

  return handles;
};

/**
 * Draw resize handles around an object
 * @param {CanvasRenderingContext2D} ctx - Canvas context
 * @param {Object} obj - Selected object
 * @param {number} scale - Current zoom scale
 * @param {Object} activeHandle - Currently active handle (if any)
 * @param {boolean} isResizing - Whether we're currently resizing
 * @param {Set<string>} [allowedHandles] - Optional set of handle types
 *   to show (e.g. new Set(['B', 'D']) for wall-mounted openings that
 *   only need the two end handles). Default: all 8 handles.
 */
export const drawResizeHandles = (
  ctx,
  obj,
  scale,
  activeHandle = null,
  isResizing = false,
  allowedHandles = null,
  options = {}
) => {
  const boundingBox = getObjectBoundingBox(obj);
  if (!boundingBox) return;
  const { visualPx: HANDLE_SIZE } = handleSizing(options.coarse);

  const allHandles = getHandlePositions(
    boundingBox,
    options.coarse ? coarseHandleOffset(scale) : 0
  );
  const handles = allowedHandles
    ? Object.fromEntries(
        Object.entries(allHandles).filter(([type]) => allowedHandles.has(type))
      )
    : allHandles;

  ctx.save();

  // If resizing, draw a preview outline with different style
  if (isResizing) {
    ctx.strokeStyle = '#10b981'; // Green for resize preview
    ctx.lineWidth = 2 / scale;
    ctx.setLineDash([8 / scale, 4 / scale]);

    ctx.beginPath();
    boundingBox.corners.forEach((corner, i) => {
      if (i === 0) {
        ctx.moveTo(corner.x, corner.z);
      } else {
        ctx.lineTo(corner.x, corner.z);
      }
    });
    ctx.closePath();
    ctx.stroke();
    ctx.setLineDash([]);

    // Also fill with semi-transparent green
    ctx.fillStyle = 'rgba(16, 185, 129, 0.1)';
    ctx.fill();
  }

  // Draw each handle
  Object.entries(handles).forEach(([type, position]) => {
    const isActive = activeHandle === type;
    // A finger gets the same blue disc as the move handle, with a two-way
    // arrow along the axis it resizes.
    if (options.coarse) {
      drawHandleDisc(ctx, position, scale, {
        glyph: handleOutwardDirection(boundingBox, type),
        active: isActive,
      });
      return;
    }

    // A mouse gets a small disc whose arrow points along the axis the
    // handle resizes — on a slanted wall the arrows follow the slant,
    // where the old plain squares said nothing.
    drawHandleDisc(ctx, position, scale, {
      glyph: handleOutwardDirection(boundingBox, type),
      active: isActive,
      radiusPx: HANDLE_SIZE / 2,
      palette: 'mouse',
    });
  });

  // Draw bounding box outline (only if not resizing, as we already drew the preview)
  if (!isResizing) {
    ctx.strokeStyle = '#0066ff';
    ctx.lineWidth = 1 / scale;
    ctx.setLineDash([5 / scale, 5 / scale]);

    ctx.beginPath();
    boundingBox.corners.forEach((corner, i) => {
      if (i === 0) {
        ctx.moveTo(corner.x, corner.z);
      } else {
        ctx.lineTo(corner.x, corner.z);
      }
    });
    ctx.closePath();
    ctx.stroke();
    ctx.setLineDash([]);
  }

  ctx.restore();
};

/**
 * Check if a point hits a resize handle
 * @param {Object} point - {x, z} point in world space
 * @param {Object} obj - Object being checked
 * @param {number} scale - Current zoom scale
 * @returns {string|null} Handle type if hit, null otherwise
 */
export const hitTestHandles = (
  point,
  obj,
  scale,
  allowedHandles = null,
  options = {}
) => {
  const boundingBox = getObjectBoundingBox(obj);
  if (!boundingBox) return null;
  const { hitPx: HANDLE_HIT_SIZE } = handleSizing(options.coarse);

  const allHandles = getHandlePositions(
    boundingBox,
    options.coarse ? coarseHandleOffset(scale) : 0
  );
  const handles = allowedHandles
    ? Object.fromEntries(
        Object.entries(allHandles).filter(([type]) => allowedHandles.has(type))
      )
    : allHandles;
  const hitAreaWorld = HANDLE_HIT_SIZE / scale; // Slightly larger than visual size for easier selection
  const halfHitArea = hitAreaWorld / 2;

  // Check each handle using square hit area
  for (const [type, position] of Object.entries(handles)) {
    // Check if point is within the square handle area
    if (
      point.x >= position.x - halfHitArea &&
      point.x <= position.x + halfHitArea &&
      point.z >= position.z - halfHitArea &&
      point.z <= position.z + halfHitArea
    ) {
      return type;
    }
  }

  return null;
};

/**
 * Calculate new dimensions based on handle drag
 * @param {Object} obj - Original object
 * @param {string} handleType - Type of handle being dragged
 * @param {Object} dragDelta - {x, z} delta in world space
 * @param {boolean} preserveAspectRatio - Whether to preserve aspect ratio
 * @returns {Object} New dimensions and center
 */
export const calculateResize = (
  obj,
  handleType,
  dragDelta,
  preserveAspectRatio = false
) => {
  const boundingBox = getObjectBoundingBox(obj);
  if (!boundingBox) return null;

  const { angle, width, depth } = boundingBox;

  // Check if object is significantly rotated (near 90° or -90°)
  const isRotated90 = Math.abs(Math.abs(angle) - Math.PI / 2) < 0.1; // Within ~6 degrees of 90°

  // Transform drag delta to local space
  // We need to rotate by -angle to go from world to local
  const cos = Math.cos(angle);
  const sin = Math.sin(angle);

  // Apply inverse rotation (transpose of rotation matrix)
  const localDeltaX = dragDelta.x * cos + dragDelta.z * sin;
  const localDeltaZ = -dragDelta.x * sin + dragDelta.z * cos;

  let newWidth = width;
  let newDepth = depth;
  let centerOffsetX = 0;
  let centerOffsetZ = 0;

  // Calculate new dimensions based on handle type
  // Note: Handle types correspond to corners after rotation,
  // but we need to resize based on the local space orientation
  switch (handleType) {
    case '1': // TOP_LEFT corner
      // This is corner[0] which is local (-halfWidth, -halfDepth)
      // When dragging this corner, moving right increases X, moving down increases Z
      if (preserveAspectRatio) {
        // Corner handle with aspect ratio preservation
        const avgDelta = (Math.abs(localDeltaX) + Math.abs(localDeltaZ)) / 2;
        const deltaX = -avgDelta * Math.sign(localDeltaX);
        const deltaZ = -avgDelta * Math.sign(localDeltaZ);
        newWidth = Math.max(0.1, width + deltaX);
        newDepth = Math.max(0.1, depth + deltaZ);
        // Maintain aspect ratio
        const ratio = width / depth;
        if (newWidth / newDepth > ratio) {
          newWidth = newDepth * ratio;
        } else {
          newDepth = newWidth / ratio;
        }
        centerOffsetX = (width - newWidth) / 2;
        centerOffsetZ = (depth - newDepth) / 2;
      } else {
        // When dragging top-left handle:
        // - Moving right (positive localDeltaX) should make width smaller
        // - Moving down (positive localDeltaZ) should make depth smaller
        newWidth = Math.max(0.1, width - localDeltaX);
        newDepth = Math.max(0.1, depth - localDeltaZ);
        centerOffsetX = (width - newWidth) / 2;
        centerOffsetZ = (depth - newDepth) / 2;
      }
      break;

    case '2': // TOP_RIGHT corner
      if (preserveAspectRatio) {
        const avgDelta = (Math.abs(localDeltaX) + Math.abs(localDeltaZ)) / 2;
        const deltaX = avgDelta * Math.sign(localDeltaX);
        const deltaZ = -avgDelta * Math.sign(localDeltaZ);
        newWidth = Math.max(0.1, width + deltaX);
        newDepth = Math.max(0.1, depth + deltaZ);
        const ratio = width / depth;
        if (newWidth / newDepth > ratio) {
          newWidth = newDepth * ratio;
        } else {
          newDepth = newWidth / ratio;
        }
        centerOffsetX = (newWidth - width) / 2;
        centerOffsetZ = (depth - newDepth) / 2;
      } else {
        newWidth = Math.max(0.1, width + localDeltaX);
        newDepth = Math.max(0.1, depth - localDeltaZ);
        centerOffsetX = (newWidth - width) / 2;
        centerOffsetZ = (depth - newDepth) / 2;
      }
      break;

    case '3': // BOTTOM_RIGHT corner
      if (preserveAspectRatio) {
        const avgDelta = (Math.abs(localDeltaX) + Math.abs(localDeltaZ)) / 2;
        const deltaX = avgDelta * Math.sign(localDeltaX);
        const deltaZ = avgDelta * Math.sign(localDeltaZ);
        newWidth = Math.max(0.1, width + deltaX);
        newDepth = Math.max(0.1, depth + deltaZ);
        const ratio = width / depth;
        if (newWidth / newDepth > ratio) {
          newWidth = newDepth * ratio;
        } else {
          newDepth = newWidth / ratio;
        }
        centerOffsetX = (newWidth - width) / 2;
        centerOffsetZ = (newDepth - depth) / 2;
      } else {
        newWidth = Math.max(0.1, width + localDeltaX);
        newDepth = Math.max(0.1, depth + localDeltaZ);
        centerOffsetX = (newWidth - width) / 2;
        centerOffsetZ = (newDepth - depth) / 2;
      }
      break;

    case '4': // BOTTOM_LEFT corner
      if (preserveAspectRatio) {
        const avgDelta = (Math.abs(localDeltaX) + Math.abs(localDeltaZ)) / 2;
        const deltaX = -avgDelta * Math.sign(localDeltaX);
        const deltaZ = avgDelta * Math.sign(localDeltaZ);
        newWidth = Math.max(0.1, width + deltaX);
        newDepth = Math.max(0.1, depth + deltaZ);
        const ratio = width / depth;
        if (newWidth / newDepth > ratio) {
          newWidth = newDepth * ratio;
        } else {
          newDepth = newWidth / ratio;
        }
        centerOffsetX = (width - newWidth) / 2;
        centerOffsetZ = (newDepth - depth) / 2;
      } else {
        newWidth = Math.max(0.1, width - localDeltaX);
        newDepth = Math.max(0.1, depth + localDeltaZ);
        centerOffsetX = (width - newWidth) / 2;
        centerOffsetZ = (newDepth - depth) / 2;
      }
      break;

    // Side handles - only resize in one dimension
    case 'A': // Handle at negative Z edge in local space
      // When dragging this handle away from center, object should get bigger
      // The handle is at -halfDepth position, so dragging to more negative should increase size
      newDepth = Math.max(0.1, depth - localDeltaZ);
      // Move center by half the change in the negative Z direction
      // When depth increases, center should move in negative Z to keep positive edge fixed
      centerOffsetZ = -(newDepth - depth) / 2;
      break;

    case 'C': // Handle at positive Z edge in local space
      // When dragging this handle away from center, object should get bigger
      // The handle is at +halfDepth position, so dragging to more positive should increase size
      newDepth = Math.max(0.1, depth + localDeltaZ);
      // Move center by half the change in the positive Z direction
      // When depth increases, center should move in positive Z to keep negative edge fixed
      centerOffsetZ = (newDepth - depth) / 2;
      break;

    case 'D': // MIDDLE_LEFT side
      // LEFT is the side with smaller X
      // Dragging left (negative localDeltaX) should make object bigger
      newWidth = Math.max(0.1, width - localDeltaX);
      // Move center by half the change in the negative X direction
      centerOffsetX = -(newWidth - width) / 2;
      break;

    case 'B': // MIDDLE_RIGHT side
      // RIGHT is the side with larger X
      // Dragging right (positive localDeltaX) should make object bigger
      newWidth = Math.max(0.1, width + localDeltaX);
      // Move center by half the change in the positive X direction
      centerOffsetX = (newWidth - width) / 2;
      break;
  }

  // Transform center offset back to world space
  const worldOffsetX = centerOffsetX * cos - centerOffsetZ * sin;
  const worldOffsetZ = centerOffsetX * sin + centerOffsetZ * cos;

  const result = {
    // Height is unchanged; every other key is kept (`resizeDimensions`).
    dimensions: resizeDimensions(obj.dimensions, {
      x: newWidth,
      y: obj.dimensions.y,
      z: newDepth,
    }),
    center: {
      x: obj.center.x + worldOffsetX,
      y: obj.center.y,
      z: obj.center.z + worldOffsetZ,
    },
  };

  return result;
};

/**
 * Get cursor style for a handle type
 * @param {string} handleType - Type of handle
 * @param {number} angle - Object rotation angle in radians
 * @returns {string} CSS cursor style
 */
export const getHandleCursor = (handleType, angle = 0) => {
  // Convert angle to degrees and normalize to 0-360
  const degrees = ((angle * 180) / Math.PI + 360) % 360;

  // Map handle types to base cursor directions (at 0 rotation)
  const baseCursors = {
    1: 'nw-resize', // TOP_LEFT
    A: 'n-resize', // TOP_CENTER
    2: 'ne-resize', // TOP_RIGHT
    D: 'w-resize', // MIDDLE_LEFT
    B: 'e-resize', // MIDDLE_RIGHT
    4: 'sw-resize', // BOTTOM_LEFT
    C: 's-resize', // BOTTOM_CENTER
    3: 'se-resize', // BOTTOM_RIGHT
  };

  // Rotate cursor based on object angle
  // We need to map the rotated direction to the nearest standard cursor
  const rotateCursor = (baseCursor, degrees) => {
    // Define the 8 cursor directions in order (clockwise from north)
    const cursorOrder = [
      'n-resize',
      'ne-resize',
      'e-resize',
      'se-resize',
      's-resize',
      'sw-resize',
      'w-resize',
      'nw-resize',
    ];

    // Map base cursors to their index
    const cursorAngles = {
      'n-resize': 0,
      'ne-resize': 1,
      'e-resize': 2,
      'se-resize': 3,
      's-resize': 4,
      'sw-resize': 5,
      'w-resize': 6,
      'nw-resize': 7,
    };

    const baseIndex = cursorAngles[baseCursor];
    if (baseIndex === undefined) return baseCursor;

    // Each 45 degrees rotates to the next cursor
    const rotationSteps = Math.round(degrees / 45);
    const newIndex = (baseIndex + rotationSteps) % 8;

    return cursorOrder[newIndex];
  };

  const baseCursor = baseCursors[handleType] || 'default';
  if (baseCursor === 'default') return baseCursor;

  return rotateCursor(baseCursor, degrees);
};
